<template>
  <div>
    <v-breadcrumbs :items="breadcrumbItems" />

    <v-container fluid>
      <v-row>
        <v-col cols="12" md="3">
          <v-card>
            <v-card-title class="text-subtitle-1">Online Users</v-card-title>
            <v-card-text class="text-h4">{{ totalUsers }}</v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="3">
          <v-card>
            <v-card-title class="text-subtitle-1">Mapped Users</v-card-title>
            <v-card-text class="text-h4">{{ mappedUsers.length }}</v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="3">
          <v-card>
            <v-card-title class="text-subtitle-1">Sockets</v-card-title>
            <v-card-text class="text-h4">{{ totalSockets }}</v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="3">
          <v-card>
            <v-card-title class="text-subtitle-1">Refresh</v-card-title>
            <v-card-text>
              <v-btn
                color="primary"
                block
                :loading="isLoading"
                @click="loadUsers"
              >
                <v-icon left>mdi-refresh</v-icon>
                Reload
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-alert v-if="error" type="error" dense text class="mt-4">
        {{ error }}
      </v-alert>

      <v-card class="mt-4">
        <v-card-title>
          Realtime Connected Users Map
          <v-spacer />
          <span class="text-caption">{{ lastLoadedLabel }}</span>
        </v-card-title>
        <v-card-text>
          <div class="map-shell">
            <img
              class="world-map"
              alt="World map"
              src="https://upload.wikimedia.org/wikipedia/commons/9/9f/BlankMap-World-Equirectangular.svg"
            />

            <button
              v-for="user in mappedUsers"
              :key="user.userId"
              class="map-marker"
              :style="markerStyle(user)"
              type="button"
              @click="selectedUser = user"
            >
              <span class="marker-dot"></span>
              <span class="marker-pulse"></span>
            </button>
          </div>
        </v-card-text>
      </v-card>

      <v-row class="mt-4">
        <v-col cols="12" lg="4">
          <v-card class="fill-height">
            <v-card-title>User Location</v-card-title>
            <v-simple-table dense>
              <template>
                <tbody>
                  <tr>
                    <td class="key-column">User ID</td>
                    <td>
                      <code>{{ selectedValue("userId") }}</code>
                    </td>
                  </tr>
                  <tr>
                    <td class="key-column">Name</td>
                    <td>{{ selectedValue("name") }}</td>
                  </tr>
                  <tr>
                    <td class="key-column">Email</td>
                    <td>{{ selectedValue("email") }}</td>
                  </tr>
                  <tr>
                    <td class="key-column">IP</td>
                    <td>{{ selectedValue("ip") }}</td>
                  </tr>
                  <tr>
                    <td class="key-column">Location</td>
                    <td>{{ selectedLocationLabel }}</td>
                  </tr>
                  <tr>
                    <td class="key-column">Socket IDs</td>
                    <td>
                      <div class="socket-list">
                        <code
                          v-for="socketId in selectedSocketIds"
                          :key="socketId"
                        >
                          {{ socketId }}
                        </code>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>

        <v-col cols="12" lg="8">
          <v-card>
            <v-card-title>Connected Users</v-card-title>
            <v-data-table
              :headers="headers"
              :items="users"
              :loading="isLoading"
              :footer-props="footerProps"
              item-key="userId"
              dense
              class="row-pointer"
              @click:row="selectedUser = $event"
            >
              <template v-slot:item.userId="{ item }">
                <code>{{ item.userId }}</code>
              </template>

              <template v-slot:item.location="{ item }">
                {{ locationLabel(item) }}
              </template>

              <template v-slot:item.socketIds="{ item }">
                <div class="socket-list">
                  <code v-for="socketId in item.socketIds" :key="socketId">
                    {{ socketId }}
                  </code>
                </div>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
export default {
  name: "ConnectedUsersMap",

  data() {
    return {
      apiBaseUrl: "",
      error: "",
      users: [],
      selectedUser: null,
      isLoading: false,
      lastLoadedAt: null,
      footerProps: {
        "items-per-page-options": [20, 100, -1],
      },
      headers: [
        { text: "User ID", value: "userId" },
        { text: "Name", value: "name" },
        { text: "Email", value: "email" },
        { text: "IP", value: "ip" },
        { text: "Location", value: "location" },
        { text: "Sockets", value: "socketCount" },
        { text: "Socket IDs", value: "socketIds", sortable: false },
      ],
    };
  },

  computed: {
    breadcrumbItems() {
      return [
        {
          text: "Connected Users Map",
          disabled: true,
        },
      ];
    },
    totalUsers() {
      return this.users.length;
    },
    totalSockets() {
      return this.users.reduce((sum, user) => sum + (user.socketCount || 0), 0);
    },
    mappedUsers() {
      return this.users.filter((user) => this.hasLocation(user));
    },
    selectedSocketIds() {
      return this.selectedUser && this.selectedUser.socketIds
        ? this.selectedUser.socketIds
        : [];
    },
    selectedLocationLabel() {
      return this.selectedUser ? this.locationLabel(this.selectedUser) : "-";
    },
    lastLoadedLabel() {
      return this.lastLoadedAt
        ? `Last loaded ${this.lastLoadedAt.toLocaleTimeString()}`
        : "Not loaded";
    },
  },

  mounted() {
    this.apiBaseUrl =
      localStorage.getItem("push_api_base_url") || window.location.origin;
    this.loadUsers();
    this.refreshTimer = window.setInterval(this.loadUsers, 30000);
  },

  beforeDestroy() {
    if (this.refreshTimer) {
      window.clearInterval(this.refreshTimer);
    }
  },

  methods: {
    hasLocation(user) {
      return (
        user &&
        user.location &&
        typeof user.location.latitude === "number" &&
        typeof user.location.longitude === "number"
      );
    },
    markerStyle(user) {
      const latitude = user.location.latitude;
      const longitude = user.location.longitude;
      const left = ((longitude + 180) / 360) * 100;
      const top = ((90 - latitude) / 180) * 100;

      return {
        left: `${Math.max(0, Math.min(100, left))}%`,
        top: `${Math.max(0, Math.min(100, top))}%`,
      };
    },
    locationLabel(user) {
      if (!this.hasLocation(user)) {
        return "Location unavailable";
      }

      return [user.location.city, user.location.region, user.location.country]
        .filter(Boolean)
        .join(", ");
    },
    selectedValue(key) {
      return this.selectedUser && this.selectedUser[key]
        ? this.selectedUser[key]
        : "-";
    },
    async loadUsers() {
      this.error = "";
      this.isLoading = true;

      try {
        const baseUrl = this.apiBaseUrl.replace(/\/+$/, "");
        const response = await fetch(
          `${baseUrl}/auth/push-notifications/online-users`
        );
        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.success === false) {
          this.error = data.msg || data.error || "Unable to load users";
          this.users = [];
          this.selectedUser = null;
          return;
        }

        this.users = data.users || [];
        this.lastLoadedAt = new Date();

        if (!this.selectedUser && this.users.length) {
          this.selectedUser = this.users[0];
        }
      } catch (error) {
        this.error = `${error.message}. Check API base URL and proxy configuration.`;
        this.users = [];
        this.selectedUser = null;
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.map-shell {
  position: relative;
  width: 100%;
  overflow: hidden;
  border: 1px solid rgba(127, 127, 127, 0.25);
  border-radius: 8px;
  background: #eef5f8;
}

.world-map {
  display: block;
  width: 100%;
  min-height: 320px;
  object-fit: contain;
}

.map-marker {
  position: absolute;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  transform: translate(-50%, -50%);
  cursor: pointer;
}

.marker-dot {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 10px;
  height: 10px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  background: #e53935;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
  z-index: 2;
}

.marker-pulse {
  position: absolute;
  top: 4px;
  left: 4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(229, 57, 53, 0.28);
  animation: pulse 1.8s ease-out infinite;
}

.key-column {
  width: 28%;
}

.row-pointer >>> tbody > tr:hover {
  cursor: pointer;
}

.socket-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 300px;
}

@keyframes pulse {
  from {
    opacity: 0.8;
    transform: scale(0.65);
  }

  to {
    opacity: 0;
    transform: scale(1.8);
  }
}
</style>
